import React, { useState, useEffect } from "react";
import axios from "axios";
import "./home.css";
import { Link } from "react-router-dom";
import { io } from "socket.io-client";

const Home = ({ logout, user, API_URL }) => {

    // ─── Константы ───────────────────────────────────────────────
    const RU_M = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
    const RU_MF = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
    const RU_D = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
    const DAYS_TO_SHOW = 5;
    const MAX_RANGE = 7;

    // ─── State ───────────────────────────────────────────────────
    const [orders, setOrders] = useState([]);
    const [filter, setFilter] = useState("all");
    const [offset, setOffset] = useState(0);      // сдвиг от сегодня (в днях)
    const [rangeStart, setRangeStart] = useState(null);   // начало выбранного диапазона
    const [rangeEnd, setRangeEnd] = useState(null);   // конец выбранного диапазона
    const [selecting, setSelecting] = useState(false);  // режим выбора второй даты
    const [modalOpen, setModalOpen] = useState(false);
    const [calMonth, setCalMonth] = useState(new Date().getMonth());
    const [calYear, setCalYear] = useState(new Date().getFullYear());
    const [hoverDate, setHoverDate] = useState(null);

    const [searchQuery, setSearchQuery] = useState("");

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // ─── Загрузка заказов ────────────────────────────────────────
    // ─── Загрузка заказов ────────────────────────────────────────
    const fetchOrders = async () => {
        try {
            const response = await axios.post(`${API_URL}/getRequests`);
            const data = Array.isArray(response.data) ? response.data : [];
            setOrders(data);
        } catch (error) {
            console.error("Ошибка загрузки заказов:", error);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [API_URL]);

    // ─── Realtime: обновление при действиях других операторов ───
    useEffect(() => {
        const socketUrl = API_URL.replace(/\/api\/?$/, ""); // из "http://host:5000/api" → "http://host:5000"
        const socket = io(socketUrl);

        socket.on("calendar:update", () => {
            fetchOrders();
        });

        return () => socket.disconnect();
    }, [API_URL]);

    // ─── Вспомогательные функции ─────────────────────────────────
    // Проходит ли заказ через поиск (имя компании / имя гостя)
    function matchesSearch(order) {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return true;

        const fields = [
            order.title,
            String(order.orderNumber ?? ""),
            order.tour,
            order.manager,
            order.tourdriver,
            order.guide,
        ];
        if (fields.some((f) => f && String(f).toLowerCase().includes(q))) return true;

        if (Array.isArray(order.guests)) {
            return order.guests.some(
                (g) => (g.name && g.name.toLowerCase().includes(q)) ||
                    (g.contacts && g.contacts.toLowerCase().includes(q))
            );
        }
        return false;
    }
    // Прибавить N дней к дате
    function addDays(date, n) {
        const result = new Date(date);
        result.setDate(result.getDate() + n);
        return result;
    }

    // Одинаковый ли день у двух дат
    function sameDay(a, b) {
        return a && b && a.toDateString() === b.toDateString();
    }

    // Дата d находится между a и b (не включая границы)
    function isBetween(d, a, b) {
        if (!a || !b) return false;
        const lo = a < b ? a : b;
        const hi = a < b ? b : a;
        return d > lo && d < hi;
    }

    // Разница в днях между двумя датами
    function diffDays(a, b) {
        return Math.abs(Math.round((b - a) / (1000 * 60 * 60 * 24)));
    }

    // Дата → строка "YYYY-MM-DD" (для сравнения с o.dateStart из базы)
    function dateToKey(date) {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    }

    // Деньги: 12345 → "12 345"
    function formatMoney(amount) {
        return Number(amount || 0).toLocaleString("ru-RU");
    }

    // Инициалы менеджера: "Иван Петров" → "ИП"
    function getInitials(name) {
        if (!name) return "—";
        const parts = name.trim().split(/\s+/);
        if (parts.length > 1) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        return (name.trim()[0] + name.trim()[name.trim().length - 1]).toUpperCase();
    }

    // Есть ли питание в заказе
    function hasFood(order) {
        return order.food && (order.food.standard || order.food.vegan || order.food.other);
    }

    // Проходит ли заказ через активный фильтр
    function matchesFilter(order) {
        if (filter === "4x4") return order.transportType === "4x4";
        if (filter === "sedan") return order.transportType === "sedan";
        if (filter === "minivan") return order.transportType === "minivan";
        if (filter === "guide") return !!order.guidetype;
        if (filter === "food") return !!hasFood(order);
        return true; // "all"
    }

    // Статус/тип заказа → { label, cls }
    // Оплата: пока финансист не обработал заказ (paidAmount === null) — 
    // показываем тип оплаты, который указал оператор.
    // Как только появилась paidAmount — показываем реальный статус.
    function getPaymentStatus(order) {
        if (order.paidAmount == null) {
            const payLabels = {
                cash: { label: "Наличка", cls: "cash" },
                transfer: { label: "Перечисление", cls: "transfer" },
                debt: { label: "Долг", cls: "debt" },
                terminal: { label: "Терминал", cls: "transfer" }, // старые заказы
            };
            return payLabels[order.paymentType] || { label: "—", cls: "debt" };
        }
        if (order.paidAmount <= 0) return { label: "Не оплачено", cls: "debt" };
        if (order.paidAmount >= order.grandTotal) return { label: "Оплачено", cls: "paid" };
        return { label: "Частично оплачено", cls: "partial" };
    }

    function getStatus(order) {
        if (order.status === 1) {
            return { label: "Отменен", cls: "status", pillCls: "status" };
        }
        const payment = getPaymentStatus(order);
        if (order.type === "sharetour") {
            return { label: payment.label, cls: "share", pillCls: payment.cls };
        }
        return { ...payment, pillCls: payment.cls };
    }

    // Все заказы для конкретного дня (с учётом фильтра)
    function getOrdersForDay(date) {
        const key = dateToKey(date);
        return orders.filter(order => order.dateStart === key && matchesFilter(order) && matchesSearch(order));
    }

    // ─── Навигация по дням ───────────────────────────────────────
    function shiftDays(direction) {
        setOffset(prev => prev + direction);
        if (rangeStart) setRangeStart(prev => addDays(prev, direction));
        if (rangeEnd) setRangeEnd(prev => addDays(prev, direction));
    }

    // ─── Выбор диапазона в календаре ────────────────────────────
    function handleDayClick(date) {
        if (!selecting) {
            // Первый клик — устанавливаем начало диапазона
            setRangeStart(date);
            setRangeEnd(date);
            setSelecting(true);
        } else {
            // Второй клик — устанавливаем конец диапазона
            let endDate = date;
            if (diffDays(rangeStart, date) >= MAX_RANGE) {
                // Обрезаем до максимума
                const direction = date > rangeStart ? MAX_RANGE - 1 : -(MAX_RANGE - 1);
                endDate = addDays(rangeStart, direction);
            }
            setRangeEnd(endDate);
            setSelecting(false);
        }
    }

    // Если наводим мышь при выборе — показываем предпросмотр диапазона
    function getHoverEnd() {
        if (!hoverDate || !selecting) return null;
        if (diffDays(rangeStart, hoverDate) >= MAX_RANGE) {
            const direction = hoverDate > rangeStart ? MAX_RANGE - 1 : -(MAX_RANGE - 1);
            return addDays(rangeStart, direction);
        }
        return hoverDate;
    }

    function isDayDisabled(date) {
        if (!selecting) return false;
        return diffDays(rangeStart, date) >= MAX_RANGE;
    }

    // ─── Заголовок над навигацией (например "11–15 июн 2026") ───
    function getRangeLabel() {
        const lo = rangeStart && rangeEnd
            ? (rangeStart < rangeEnd ? rangeStart : rangeEnd)
            : rangeStart;
        const hi = rangeStart && rangeEnd
            ? (rangeStart < rangeEnd ? rangeEnd : rangeStart)
            : null;

        if (!lo) {
            const d1 = addDays(today, offset);
            const d2 = addDays(today, offset + DAYS_TO_SHOW - 1);
            if (d1.getMonth() === d2.getMonth()) {
                return `${d1.getDate()}–${d2.getDate()} ${RU_M[d1.getMonth()]} ${d1.getFullYear()}`;
            }
            return `${d1.getDate()} ${RU_M[d1.getMonth()]} – ${d2.getDate()} ${RU_M[d2.getMonth()]} ${d2.getFullYear()}`;
        }

        if (lo.getMonth() === (hi || lo).getMonth()) {
            return `${lo.getDate()}–${(hi || lo).getDate()} ${RU_M[lo.getMonth()]} ${lo.getFullYear()}`;
        }
        return `${lo.getDate()} ${RU_M[lo.getMonth()]} – ${(hi || lo).getDate()} ${RU_M[(hi || lo).getMonth()]} ${(hi || lo).getFullYear()}`;
    }

    // ─── Дни для отображения в нижней ленте ─────────────────────
    function getDisplayedDays() {
        if (rangeStart && rangeEnd) {
            const start = rangeStart < rangeEnd ? rangeStart : rangeEnd;
            const end = rangeStart < rangeEnd ? rangeEnd : rangeStart;
            const days = [];
            let current = new Date(start);
            while (current <= end) {
                days.push(new Date(current));
                current.setDate(current.getDate() + 1);
            }
            return days;
        }
        return Array.from({ length: DAYS_TO_SHOW }, (_, i) => addDays(today, offset + i));
    }

    const displayedDays = getDisplayedDays();

    // ─── Счётчики для шапки (без учёта фильтра) ─────────────────
    const displayedKeys = new Set(displayedDays.map(dateToKey));
    const visibleOrders = orders.filter(order => displayedKeys.has(order.dateStart) && matchesSearch(order));

    const counts = {
        total: visibleOrders.length,
        paid: visibleOrders.filter(o => getPaymentStatus(o).cls === "paid").length,
        partial: visibleOrders.filter(o => getPaymentStatus(o).cls === "partial").length,
        cash: visibleOrders.filter(o => o.paymentType === "cash").length,
        transfer: visibleOrders.filter(o => o.paymentType === "transfer").length,
        debt: visibleOrders.filter(o => getPaymentStatus(o).cls === "debt").length,
        share: visibleOrders.filter(o => o.type === "sharetour").length,
        toyota: visibleOrders.filter(o => o.transportType === "4x4").length,
        sedan: visibleOrders.filter(o => o.transportType === "sedan").length,
        minivan: visibleOrders.filter(o => o.transportType === "minivan").length,
        guide: visibleOrders.filter(o => o.guidetype).length,
        food: visibleOrders.filter(o => hasFood(o)).length,
        cancel: visibleOrders.filter(o => o.status === 1).length,
    };

    // ─── Построение сетки календаря ─────────────────────────────
    const firstDayOfMonth = new Date(calYear, calMonth, 1);
    const lastDayOfMonth = new Date(calYear, calMonth + 1, 0);
    const startWeekday = (firstDayOfMonth.getDay() + 6) % 7; // пн=0

    const calendarCells = [];
    for (let i = 0; i < startWeekday; i++) calendarCells.push(null); // пустые ячейки
    for (let d = 1; d <= lastDayOfMonth.getDate(); d++) {
        calendarCells.push(new Date(calYear, calMonth, d));
    }

    const rangeLo = rangeStart && rangeEnd ? (rangeStart < rangeEnd ? rangeStart : rangeEnd) : rangeStart;
    const rangeHi = rangeStart && rangeEnd ? (rangeStart < rangeEnd ? rangeEnd : rangeStart) : null;
    const hoverEnd = getHoverEnd();

    // ─── Кнопки фильтров ────────────────────────────────────────
    const filterButtons = [
        { key: "all", icon: null, label: "Все", count: counts.total },
        { key: "4x4", icon: "fa-truck-monster", label: "4х4", count: counts.toyota },
        { key: "sedan", icon: "fa-taxi", label: "Седан", count: counts.sedan },
        { key: "minivan", icon: "fa-van-shuttle", label: "Микроавтобус", count: counts.minivan },
        { key: "guide", icon: "fa-user", label: "Гиды", count: counts.guide },
        { key: "food", icon: "fa-utensils", label: "Питание", count: counts.food },
    ];

    // ─── Карточка одного заказа ──────────────────────────────────
    function renderOrderCard(order) {
        const status = getStatus(order);
        const isPartial = status.cls === "partial";
        const remaining = isPartial ? order.grandTotal - order.paidAmount : 0;
        return (
            <div key={`${order.type}-${order.id}`} className={`ocard ocard--${status.cls}`}>

                <div className="ocard__top">
                    <span title={`Имя: ${order.title}`} className="ocard__title">{order.title}</span>
                    <span className={`ocard__pill ocard__pill--${status.pillCls}`}>{status.label}</span>
                </div>

                <div className="ocard__meta">
                    <span className="ocard__tour">{order.tour} · {order.participantsCount} чел.</span>
                    <span className="ocard__num">№{order.orderNumber}</span>
                </div>
                {order.type === "sharetour" && order.guests?.length > 0 && (
                    <div className="ocard__guests">
                        {order.guests
                            .filter(guest => guest.name)
                            .map((guest, i) => (
                                <span key={i} className="ocard__guest">{i + 1}. {guest.name}</span>
                            ))
                        }
                    </div>
                )}


                {order.type === "sharetour" && order.guests?.length > 0 ? (
                    <div className="ocard__loc">
                        {order.guests
                            .filter(guest => guest.name)
                            .map((guest, i) => (
                                <div key={i} className="ocard__loc">
                                    <i className="fa-solid fa-location-dot"></i>
                                    {guest.departurePlace}
                                    <i class="fa-regular fa-clock"></i>
                                    {guest.departureTime}
                                </div>
                            ))
                        }
                    </div>
                ) : (order.departurePlace || order.departureTime) ? (
                    <div className="ocard__loc">
                        <i className="fa-solid fa-location-dot"></i>
                        {order.departurePlace}
                        <i class="fa-regular fa-clock"></i>
                        {order.departureTime}
                    </div>
                ) : null}
                <div className="ocard__pay">Гид: {
                    order.guidetype === 1
                        ? order.guide === null
                            ? "не выбран гид"
                            : order.guide
                        : "нет"
                }</div>
                <div className="ocard__pay">Водители: {order.tourdriver || "-"}</div>
                <div className="ocard__pay">Кафе: {order.cafe || "-"}</div>

                <div className="ocard__pay">
                    Оплата: {formatMoney(order.grandTotal)}$
                    {isPartial && (
                        <span className="ocard__remain"> (осталось: {formatMoney(remaining)}$)</span>
                    )}
                </div>

                <div className="ocard__foot">
                    <span className="ocard__avatar">{getInitials(order.manager)}</span>
                    <span className="ocard__manager">{order.manager}</span>
                    <Link className="ocard__more" to={`/tourdetails/${order.orderNumber}`}>
                        Подробнее <i className="fa-solid fa-arrow-right-long"></i>
                    </Link>
                </div>

            </div>
        );
    }

    // ─── Навигация по месяцам в календаре ───────────────────────
    function prevMonth() {
        if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
        else setCalMonth(m => m - 1);
    }
    function nextMonth() {
        if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
        else setCalMonth(m => m + 1);
    }

    // ─── Рендер ──────────────────────────────────────────────────
    return (
        <div className="home">
            <div className="home-flex">

                {/* Шапка */}
                <div className="home-sec1">
                    <div className="home-sec1-box1">
                        <span>
                            <i className="fa-solid fa-magnifying-glass"></i>
                            <input
                                type="text"
                                placeholder="ФИО / Номер заказа"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <i
                                    className="fa-solid fa-xmark"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setSearchQuery("")}
                                ></i>
                            )}
                        </span>
                    </div>
                    <div className="home-sec1-box2">
                        <h2>Календарь Заказов</h2>
                    </div>
                    <div className="home-sec1-box3">
                        <Link className="link" to="/addrequest">
                            <i className="fa-solid fa-square-plus"></i>
                            Добавить заказ
                        </Link>
                    </div>
                </div>

                {/* Фильтры + навигация по датам */}
                <div className="home-sec2">

                    <div className="home-sec2-box1">
                        {filterButtons.map(btn => (
                            <div
                                key={btn.key}
                                className={`home-sec2-box1-button${filter === btn.key ? " active" : ""}`}
                                onClick={() => setFilter(btn.key)}
                            >
                                {btn.icon && <span><i className={`fa-solid ${btn.icon}`}></i></span>}
                                <span>{btn.label}</span>
                                <span>{btn.count}</span>
                            </div>
                        ))}
                    </div>

                    <div className="home-sec2-box2">
                        <div className="calendar-widget">

                            {/* Навигация < Дата > */}
                            <div className="cal-nav">
                                <button onClick={() => shiftDays(-1)}>{"<"}</button>
                                <span className="cal-label" onClick={() => setModalOpen(true)}>
                                    {getRangeLabel()}
                                </span>
                                <button onClick={() => shiftDays(1)}>{">"}</button>
                            </div>

                            {/* Модалка с календарём */}
                            {modalOpen && (
                                <div
                                    className="modal-overlay"
                                    onClick={e => { if (e.target.classList.contains("modal-overlay")) setModalOpen(false); }}
                                >
                                    <div className="modal-box">

                                        <div className="modal-head">
                                            <div className="modal-mnav">
                                                <button onClick={prevMonth}>{"<"}</button>
                                                <span>
                                                    {RU_MF[calMonth].charAt(0).toUpperCase() + RU_MF[calMonth].slice(1)} {calYear}
                                                </span>
                                                <button onClick={nextMonth}>{">"}</button>
                                            </div>
                                            <button className="modal-close" onClick={() => setModalOpen(false)}>✕</button>
                                        </div>

                                        {selecting && (
                                            <div className="modal-hint">выберите конечную дату (макс. 7 дней)</div>
                                        )}

                                        <div className="cal-grid">
                                            {RU_D.map(dayName => (
                                                <div key={dayName} className="cal-dow">{dayName}</div>
                                            ))}
                                            {calendarCells.map((date, i) => {
                                                if (!date) return <div key={i} />;

                                                const isStart = sameDay(date, rangeLo);
                                                const isEnd = rangeHi && sameDay(date, rangeHi);
                                                const inRange = rangeLo && isBetween(date, rangeLo, rangeHi || hoverEnd);
                                                const isToday = sameDay(date, today);
                                                const disabled = isDayDisabled(date);

                                                let cls = "cal-day";
                                                if (disabled) cls += " disabled";
                                                else if (isStart || isEnd) cls += " range-endpoint";
                                                else if (inRange) cls += " range-mid";
                                                if (isToday) cls += " today";

                                                return (
                                                    <div
                                                        key={i}
                                                        className={cls}
                                                        onClick={() => !disabled && handleDayClick(date)}
                                                        onMouseEnter={() => { if (selecting) setHoverDate(date); }}
                                                        onMouseLeave={() => setHoverDate(null)}
                                                    >
                                                        {date.getDate()}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="modal-footer">
                                            <span className="range-text">
                                                {rangeLo
                                                    ? `${rangeLo.getDate()} ${RU_MF[rangeLo.getMonth()]}${rangeHi && !sameDay(rangeLo, rangeHi) ? ` – ${rangeHi.getDate()} ${RU_MF[rangeHi.getMonth()]}` : ''}`
                                                    : "выберите даты"
                                                }
                                            </span>
                                            <div style={{ display: "flex", gap: 8 }}>
                                                <button className="btn-clear" onClick={() => {
                                                    setRangeStart(null);
                                                    setRangeEnd(null);
                                                    setOffset(0);
                                                    setSelecting(false);
                                                }}>
                                                    Сбросить
                                                </button>
                                                <button className="btn-apply" onClick={() => setModalOpen(false)}>
                                                    Применить
                                                </button>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                </div>

                {/* Счётчики */}
                <div className="home-sec3">
                    <div className="home-sec3-box1">
                        <div className="home-sec3-box1-info"><span></span><span>Оплачено</span>    <span>{counts.paid}</span></div>
                        <div className="home-sec3-box1-info"><span></span><span>Частично оплачено</span><span>{counts.partial}</span></div>
                        <div className="home-sec3-box1-info"><span></span><span>Наличка</span>     <span>{counts.cash}</span></div>
                        <div className="home-sec3-box1-info"><span></span><span>Перечисление</span><span>{counts.transfer}</span></div>
                        <div className="home-sec3-box1-info"><span></span><span>Долг</span>        <span>{counts.debt}</span></div>
                        <div className="home-sec3-box1-info"><span></span><span>Отмена</span>      <span>{counts.cancel}</span></div>
                        <div className="home-sec3-box1-info"><span></span><span>Share Tour</span>  <span>{counts.share}</span></div>
                    </div>
                    <div className="home-sec3-box2">
                        <div className="home-sec3-box2-info">
                            <span><i className="fa-solid fa-circle-exclamation"></i></span>
                            <span>{counts.debt}</span>
                            <span>Долгов на эти дни</span>
                        </div>
                    </div>
                </div>

                {/* Лента дней с заказами */}
                <div className="home-sec4">
                    <div className="days-row days-row--orders">
                        {displayedDays.map((day, i) => {
                            const weekdayIndex = (day.getDay() + 6) % 7;
                            const dayOrders = getOrdersForDay(day);
                            return (
                                <div key={i} className="order-day">
                                    <div className="order-day__head">
                                        <div className="order-day__date">
                                            <span className="day-name">{RU_D[weekdayIndex]}</span>
                                            <span className="day-num">{day.getDate()}</span>
                                        </div>
                                        <span className={`order-day__badge${dayOrders.length ? " is-active" : ""}`}>
                                            {dayOrders.length}
                                        </span>
                                    </div>

                                    <div className="order-day__orders">
                                        {dayOrders.length === 0 ? (
                                            <div className="day-empty">
                                                <span>Нет заказов</span>
                                                <Link to="/addrequest" className="day-empty__add">+ Добавить</Link>
                                            </div>
                                        ) : (
                                            dayOrders.map(order => renderOrderCard(order))
                                        )}
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Home;